<template>
  <v-sheet class="month-grid" rounded="lg" outlined>
    <div class="month-grid__header">
      <div class="month-grid__title">{{ monthTitle }}</div>
      <div class="d-flex align-center">
        <v-btn text small class="text-capitalize mr-1" @click="goToday">Сегодня</v-btn>
        <v-btn icon small @click="shiftMonth(-1)">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon small @click="shiftMonth(1)">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="month-grid__weekdays">
      <span v-for="label in weekdayLabels" :key="label">{{ label }}</span>
    </div>

    <div class="month-grid__days">
      <button
        v-for="day in days"
        :key="day.date"
        type="button"
        class="month-grid__day"
        :class="{
          'month-grid__day--muted': !day.inCurrentMonth,
          'month-grid__day--has-slots': day.hasSlots && !(disablePast && day.isPast),
          'month-grid__day--selected': day.date === selectedDate,
          'month-grid__day--past': disablePast && day.isPast,
        }"
        @click="onDayClick(day)"
      >
        <span class="month-grid__day-number">
          {{ day.dayNumber }}
        </span>
        <span v-if="day.free" class="month-grid__badge month-grid__badge--free">{{ day.free }} своб.</span>
        <span v-if="day.booked" class="month-grid__badge month-grid__badge--booked">{{ day.booked }} занято</span>
      </button>
    </div>
  </v-sheet>
</template>

<script>
import { utcToLocalDate } from '@/common/calendarTime';

export default {
  name: 'MonthGrid',
  props: {
    month: { type: String, required: true }, // 'YYYY-MM'
    selectedDate: { type: String, default: '' },
    availability: { type: Array, default: () => [] }, // открытые интервалы (Availability, status === 'open')
    appointments: { type: Array, default: () => [] }, // брони (Appointment)
    // Для гостевого календаря, где нет сводки по месяцу (см. store/publicCalendar.js,
    // loadMonthAvailability) — просто список дат "тут точно есть что забронировать".
    slotDates: { type: Array, default: () => [] },
    // В админке прошлые дни надо смотреть (история броней), в гостевом флоу — бронировать
    // прошлое бессмысленно, поэтому визуально гасим и не даём кликать только там, где явно попросили.
    disablePast: { type: Boolean, default: false },
  },
  data() {
    return {
      weekdayLabels: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    };
  },
  computed: {
    monthTitle() {
      return this.$moment(this.month, 'YYYY-MM').locale('ru').format('MMMM YYYY');
    },
    days() {
      const monthStart = this.$moment(this.month, 'YYYY-MM').locale('ru').startOf('month');
      const gridStart = monthStart.clone().startOf('isoWeek');
      const monthEnd = monthStart.clone().endOf('month');
      const gridEnd = monthEnd.clone().endOf('isoWeek');

      const freeByDate = this.availability.reduce((acc, item) => {
        const date = utcToLocalDate(item.starts_at);
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      const bookedByDate = this.appointments.reduce((acc, item) => {
        const date = utcToLocalDate(item.scheduled_starts_at);
        acc[date] = (acc[date] || 0) + 1;
        return acc;
      }, {});

      const slotDatesSet = new Set(this.slotDates);
      const today = this.$moment().format('YYYY-MM-DD');

      const result = [];
      const cursor = gridStart.clone();
      while (cursor.isSameOrBefore(gridEnd, 'day')) {
        const dateStr = cursor.format('YYYY-MM-DD');
        result.push({
          date: dateStr,
          dayNumber: cursor.date(),
          inCurrentMonth: cursor.isSame(monthStart, 'month'),
          free: freeByDate[dateStr] || 0,
          booked: bookedByDate[dateStr] || 0,
          hasSlots: slotDatesSet.has(dateStr),
          isPast: dateStr < today,
        });
        cursor.add(1, 'day');
      }
      return result;
    },
  },
  methods: {
    shiftMonth(delta) {
      const next = this.$moment(this.month, 'YYYY-MM').add(delta, 'month').format('YYYY-MM');
      this.$emit('update:month', next);
    },
    goToday() {
      const today = this.$moment().format('YYYY-MM');
      this.$emit('update:month', today);
      this.$emit('select-date', this.$moment().format('YYYY-MM-DD'));
    },
    onDayClick(day) {
      if (this.disablePast && day.isPast) return;
      this.$emit('select-date', day.date);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_tokens.scss';

.month-grid {
  padding: $space-4;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: $space-3;
  }

  &__title {
    font-weight: $weight-semibold;
    font-size: $text-lg;
    letter-spacing: -0.01em;
    text-transform: capitalize;
    text-align: left;
    flex: 1 1 auto;
  }

  ::v-deep .v-btn {
    border-radius: $radius-sm;
    transition: background-color $transition-fast;
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: left;
    font-size: $text-xs;
    color: #8a8f9c;
    padding-bottom: $space-2;
    margin-bottom: $space-2;
    border-bottom: 1px solid #e5e7ec;

    span {
      padding-left: $space-2;
    }
  }

  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: $space-1;
  }

  &__day {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    border: 1px solid #e5e7ec;
    border-radius: $radius-sm;
    padding: $space-2;
    min-height: 64px;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background-color $transition-fast, border-color $transition-fast;

    &:hover {
      background: #f5f6fa;
    }

    &--muted {
      opacity: 0.4;
    }

    &--has-slots {
      background: #eafaf1;

      &:hover {
        background: #ddf4e8;
      }
    }

    &--selected {
      border-color: #483CB9;
      background: #f0eefc;
    }

    &--past {
      color: #b5b8c2;
      cursor: default;
      background: transparent;

      &:hover {
        background: transparent;
      }
    }
  }

  &__day-number {
    font-size: $text-sm;
    font-weight: $weight-medium;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__badge {
    font-size: 10px; // мельче общей шкалы токенов, осознанно — компактная пилюля внутри ячейки дня
    border-radius: $radius-xs;
    padding: 2px 6px;

    &--free {
      background: #e3f6ec;
      color: #009E60;
    }

    &--booked {
      background: #fdecec;
      color: #EC2E2E;
    }
  }
}
</style>
